import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'
import { planFeatures } from '@/config/recharge';
import { Button } from '../LinkButton';
import { useDocumentVisibility, useRequest } from 'ahooks';
import { getPlanList } from '@/service/subscription';
import { UpgradeButton } from '../..';


const Icons = {
  Info: ({ className }: { className?: string }) => (
    <svg className={cn("w-4 md:w-6", className)} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM13.5 8.25C13.5 9.07843 12.8284 9.75 12 9.75C11.1716 9.75 10.5 9.07843 10.5 8.25C10.5 7.42157 11.1716 6.75 12 6.75C12.8284 6.75 13.5 7.42157 13.5 8.25ZM13 10.75V15.25H14V17.25H10V15.25H11V12.75H10V10.75H13Z" fill="#CECFD3"></path></svg>
  ),
  PricingTick: ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="8" fill="#FF5B27"></rect><path d="M4.5 8L7 10.5L11.5 6" stroke="white" strokeWidth="1.5"></path></svg>
  ),
  Enterprise: ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><g clipPath="url(#clip0_945_4853)"><path d="M16 46.0001H19.9995V41.9991H24.001V46.0001H28.0005V38.0002H16V46.0001Z" fill="#111112"></path><path d="M35.9995 38.0002H32V42.0012H35.9995V38.0002Z" fill="#111112"></path><path d="M8.00101 33.9995V30.0006H16V25.9997H8.00101V22.0008H16V17.9999H8.00101V14.001H3.9995V22.0008H0V41.9993H3.9995V46.0002H12.0005V38.0004H16V33.9995H8.00101Z" fill="#111112"></path><path d="M36.0015 30.0005H28.0005V34.0014H36.0015V30.0005Z" fill="#111112"></path><path d="M24.001 25.9993V5.99878H16V9.99969H19.9995V14.0006H16V17.9995H19.9995V22.0004H16V25.9993H19.9995V30.0002H16V33.9991H24.001V30.0002H28.0005V25.9993H24.001Z" fill="#111112"></path><path d="M44.0005 14.001V33.9995H35.9995V38.0004H40.001V46.0002H44.0005V38.0004H48V14.001H44.0005Z" fill="#111112"></path><path d="M44.0005 9.99976H35.9995V13.9987H44.0005V9.99976Z" fill="#111112"></path><path d="M16.002 9.99976H8.00098V13.9987H16.002V9.99976Z" fill="#111112"></path><path d="M36.0015 5.99878H28.0005V9.99969H36.0015V5.99878Z" fill="#111112"></path><path d="M28.0005 2H24.001V5.9989H28.0005V2Z" fill="#111112"></path></g><defs><clipPath id="clip0_945_4853"><rect width="48" height="44" fill="white" transform="translate(0 2)"></rect></clipPath></defs></svg>
  ),
};

export default function PlanFeatures() {
  const { data, run: runPlanList } = useRequest(getPlanList, {
    manual: false,
  });
  const documentVisibility = useDocumentVisibility();

  useEffect(() => {
    if (documentVisibility === 'visible') {
      runPlanList();
    }
  }, [documentVisibility]);
  const currenActivePrice =
    ((data?.data ?? []).find(({ is_active }) => is_active)?.price || 0) / 100;
  const backendPlans = data?.data
  const featureSections = planFeatures.sections;
  const isYearly = false
  const plans = planFeatures.plans.map((plan) => {
    const backendPlan = backendPlans?.find(p => p.key === plan.key);
    if (!backendPlan) {
      return
    }
    return {
      ...plan,
      price: (backendPlan?.price || 0) / 100,
      is_active: backendPlan?.is_active
    }
  }).filter(i => !!i)


  return (
    <div className='w-full flex flex-col bg-white/60 rounded-lg '>
      <div className="overflow-x-auto rounded-lg">
        {featureSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="">
            <table className="min-w-full">
              <thead className='table-header-group'>
                <tr className='h-full'>
                  <th className="w-[270px]">
                  </th>
                  {plans.map((plan, sectionIndex) => {
                    const price = typeof plan.price === 'number' ? (isYearly ? (plan.price * 0.85).toFixed(2) : plan.price.toFixed(2)) : plan.price;
                    return (
                      <th key={sectionIndex} className={`w-1/5 min-w-[150px] font-normal items-center text-center md:text-left p-6 border-l-[2px] border-jen-light ${plan.title == 'Dabbler' ? 'bg-white' : ''}`}>
                        <img src={plan.icon} alt="Plan Icon" width="42" height="42" className='mx-auto md:mx-0' />
                        <p className='text-lg md:text-xl mt-6'>{plan.title}</p>
                        <p className='hidden md:flex text-base text-jen-content-secondary whitespace-nowrap mb-4'>{`USD $${price}/month`}</p>

                        <UpgradeButton
                          skuKey={plan.key}
                          currenActivePrice={currenActivePrice}
                          price={plan.price}
                          isActive={plan.is_active}
                          className="mt-jen-spacing-16"
                        />
                      </th>
                    )

                  })}
                </tr>
              </thead>
              <tbody className='border-t-[2px] border-jen-light'>
                {section.features.map((feature, featureIndex) => (
                  <tr key={featureIndex} className='text-xs md:text-sm border-b-[2px] border-jen-light'>
                    <td className="w-1/5 p-3 flex items-center md:whitespace-nowrap ">
                      {feature.title}
                      <span
                        data-tooltip-id="tooltip"
                        data-tooltip-content={feature.tooltip}
                        data-tooltip-delay-hide={100}
                        className=" ml-1 md:ml-2">
                        <Icons.Info className="w-4 md:w-6" />
                      </span>
                      <Tooltip id="tooltip" place="right" />
                    </td>
                    {
                      plans.some(({ key }) => key === 'base') && <td className={`w-1/5 text-center p-3 border-x-[2px] border-jen-light ${feature.Novice === 'No' || feature.Novice === 'No Downloads' || feature.Novice === 'Limit of 4' || feature.Novice === '1' ? 'opacity-30' : ''}`}>
                        {feature.Novice === 'Yes' ? (<div className='flex flex-row justify-center items-center gap-2'><Icons.PricingTick />Yes</div>) : feature.Novice}
                      </td>
                    }
                    {
                      plans.some(({ key }) => key === 'dabbler') && <td className={`bg-white w-1/5 text-center p-3 border-x-[2px] border-jen-light ${feature.Dabbler === 'No' || feature.Dabbler === '1' ? 'text-black/30' : ''}`}>
                        {feature.Dabbler === 'Yes' ? (<div className='flex flex-row justify-center items-center gap-2'><Icons.PricingTick />Yes</div>) : feature.Dabbler}
                      </td>
                    }

                    {
                      plans.some(({ key }) => key === 'soloist') && <td className={`w-1/5 text-center p-3 border-x-[2px] border-jen-light ${feature.Soloist === 'No' ? 'opacity-30' : ''}`}>
                        {feature.Soloist === 'Yes' || feature.Soloist === 'Up to 5' ? (<div className='flex flex-row justify-center items-center gap-2'><Icons.PricingTick />{feature.Soloist}</div>) : feature.Soloist}
                      </td>
                    }
                    {
                      plans.some(({ key }) => key === 'pro') && <td className={`w-1/5 text-center p-3 border-l-[2px] border-jen-light ${feature.Pro === 'No' ? 'opacity-30' : ''}`}>
                        {feature.Pro === 'Yes' || feature.Pro === 'Up to 25' ? (<div className='flex flex-row justify-center items-center gap-2'><Icons.PricingTick />{feature.Pro}</div>) : feature.Pro}
                      </td>
                    }

                  </tr>
                ))}
              </tbody>
            </table>

          </div>
        ))}
      </div>
      <div className='flex flex-col items-center md:items-start md:flex-row px-6 py-8 gap-6'>
        <Icons.Enterprise />
        <div className='flex flex-col w-full items-center md:items-start text-center md:text-left'>
          <p className='text-xs uppercase font-secondary'>Need more music?</p>
          <p className='text-lg'>Get in touch about Custom Plans.</p>
        </div>
        <Button className='whitespace-nowrap w-[170px]' target='_blank' link='https://feedback.jenmusic.ai/api-access'>Get in Touch</Button>
      </div>
    </div>
  );
}




